import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import ScheduleService from "@/services/hrm/schedule.service";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const useScheduleStore = defineStore("schedules", () => {
  const rows = ref([]);
  const loading = ref(false);
  const pagination = reactive({ page: 1, per_page: 25, total: 0 });

  const filters = reactive({
    date_from: "",
    date_to: "",
    department_id: "",
    employee_id: "",
    per_page: 25,
    page: 1,
  });

  async function fetchList(params = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_ATTENDANCE))) {
      rows.value = [];
      pagination.page = 1;
      pagination.per_page = 25;
      pagination.total = 0;
      return;
    }

    loading.value = true;
    try {
      const res = await ScheduleService.getAll({ ...filters, ...params });
      rows.value = res.data?.data || [];
      const meta = res.data?.meta;
      if (meta) {
        pagination.page = meta.current_page;
        pagination.per_page = meta.per_page;
        pagination.total = meta.total;
      }
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to load schedules", "error");
    } finally {
      loading.value = false;
    }
  }

  async function create(payload) {
    loading.value = true;
    try {
      await ScheduleService.create(payload);
      toast("Schedule created", "success");
      await fetchList({ page: 1 });
    } catch (e) {
      toast(e?.response?.data?.message || "Create failed", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      await ScheduleService.update(id, payload);
      toast("Schedule updated", "success");
      await fetchList();
    } catch (e) {
      toast(e?.response?.data?.message || "Update failed", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    try {
      await ScheduleService.delete(id);
      toast("Schedule deleted", "success");
      await fetchList();
    } catch (e) {
      toast(e?.response?.data?.message || "Delete failed", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    loading.value = true;
    try {
      await ScheduleService.bulkDelete(ids);
      toast("Selected schedules deleted", "success");
      await fetchList();
    } catch (e) {
      toast(e?.response?.data?.message || "Bulk delete failed", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    rows,
    loading,
    pagination,
    filters,
    fetchList,
    create,
    update,
    remove,
    bulkDelete,
  };
});
